import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Loader2, Save, Send, RefreshCw, CalendarClock, HeartHandshake } from "lucide-react";
import { AppShell } from "@/components/app/AppShell";
import { UpdateReader } from "@/components/UpdateReader";
import { ListEditor } from "@/components/ListEditor";
import { CommentsThread } from "@/components/CommentsThread";
import { UpdateDeliverySettings, DEFAULT_DELIVERY_SETTINGS, DELIVERY_LABELS } from "@/components/UpdateDeliverySettings";
import { TenXBrandFooter } from "@/components/TenXBrandFooter";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { MonthPicker } from "@/components/MonthPicker";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { METRIC_DEFS } from "@/lib/metrics";
import { formatMetric, formatPeriodLabel } from "@/lib/format";

function currentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export default function UpdateComposer() {
  const { company } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const prefill = location.state?.prefill;

  const [updateId, setUpdateId] = useState(id || null);
  const [status, setStatus] = useState("draft");
  const [period, setPeriod] = useState(prefill?.period || currentMonth());
  const [title, setTitle] = useState("");
  const [tldr, setTldr] = useState("");
  const [highlights, setHighlights] = useState(prefill?.structured?.highlights || []);
  const [lowlights, setLowlights] = useState(prefill?.structured?.lowlights || []);
  const [asks, setAsks] = useState(prefill?.structured?.asks || []);
  const [body, setBody] = useState("");
  const [snapshot, setSnapshot] = useState(prefill?.structured?.metrics || {});
  const [investorsCount, setInvestorsCount] = useState(0);
  const [templates, setTemplates] = useState([]);
  const [deliverySettings, setDeliverySettings] = useState(DEFAULT_DELIVERY_SETTINGS);

  const [loading, setLoading] = useState(!!id);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [scheduledFor, setScheduledFor] = useState("");
  const [scheduling, setScheduling] = useState(false);

  // load existing update
  useEffect(() => {
    if (!id) return;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/updates/${id}`);
        setStatus(data.status); setPeriod(data.period); setTitle(data.title || "");
        setTldr(data.tldr || ""); setHighlights(data.highlights || []);
        setLowlights(data.lowlights || []); setAsks(data.asks || []);
        setBody(data.body_markdown || ""); setSnapshot(data.metrics_snapshot || {});
        setDeliverySettings({ ...DEFAULT_DELIVERY_SETTINGS, ...data.delivery_settings });
        setScheduledFor(data.scheduled_for ? String(data.scheduled_for).slice(0, 16) : "");
      } catch (e) { toast.error("Could not load update"); navigate("/app/updates"); }
      finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (!company) return;
    api.get(`/companies/${company.id}/investors`).then(({ data }) => setInvestorsCount(data.length)).catch((e) => console.error("investors load failed", e));
    if (!id && !prefill?.period) {
      api.get(`/companies/${company.id}/metrics`).then(({ data }) => {
        const periods = data.map((m) => m.period).filter(Boolean).sort();
        if (periods.length && !periods.includes(currentMonth())) setPeriod(periods[periods.length - 1]);
      }).catch(() => {});
    }
    api.get(`/companies/${company.id}/templates`).then(({ data }) => setTemplates(data)).catch((e) => console.error("templates load failed", e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company]);

  const applyTemplate = (tid) => {
    const t = templates.find((x) => x.id === tid);
    if (!t) return;
    if (t.default_asks?.length) setAsks((a) => Array.from(new Set([...(a || []), ...t.default_asks])));
    if (t.body_skeleton && !body.trim()) setBody(t.body_skeleton);
    toast.success(`Applied template "${t.name}"`);
  };

  const pullMetrics = useCallback(async () => {
    if (!company) return;
    try {
      const { data } = await api.get(`/companies/${company.id}/metrics`);
      const row = data.find((m) => m.period === period);
      if (!row) { toast.warning(`No saved metrics for ${formatPeriodLabel(period)}. Add them on the Metrics page.`); return; }
      const snap = {}; METRIC_DEFS.forEach((d) => { snap[d.key] = row[d.key] ?? null; });
      setSnapshot(snap);
      toast.success(`Pulled metrics for ${formatPeriodLabel(period)}`);
    } catch { toast.error("Could not load metrics"); }
  }, [company, period]);

  const readOnly = status === "published";

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/ai/draft-update`, {
        company_id: company.id, period, metrics: snapshot, highlights, lowlights, asks,
      });
      setBody(data.body_markdown);
      if (!tldr && data.tldr) setTldr(data.tldr);
      toast.success("AI drafted your update. Edit anything you like.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "AI draft failed");
    } finally { setGenerating(false); }
  };

  const buildPayload = () => ({
    period, title: title || `Update - ${period}`, tldr, highlights, lowlights, asks,
    body_markdown: body, metrics_snapshot: snapshot,
    ...(status !== "imported" ? { delivery_settings: deliverySettings } : {}),
  });

  const insertSweatEquity = async () => {
    try {
      const { data } = await api.get(`/companies/${company.id}/sweat-equity`);
      const s = data.summary || {};
      if (!s.total_accrued) { toast.info("Add contributors on the Sweat Equity page first"); return; }
      const money = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);
      const runway = s.runway_months_saved ? ` - the equivalent of ${s.runway_months_saved} months of runway saved` : "";
      const line = `\n\n**Founder sweat equity:** Our team has invested ${money(s.total_accrued)} in below-market compensation to date (${money(s.monthly_run_rate)}/mo across ${s.people} contributor${s.people === 1 ? "" : "s"})${runway}.\n`;
      setBody((b) => (b || "") + line);
      toast.success("Sweat equity summary added to the update");
    } catch { toast.error("Could not load sweat equity"); }
  };

  const save = async ({ silent } = {}) => {
    const payload = buildPayload();
    let uid = updateId;
    if (updateId) { await api.put(`/updates/${updateId}`, payload); }
    else { const { data } = await api.post(`/companies/${company.id}/updates`, payload); uid = data.id; setUpdateId(uid); window.history.replaceState(null, "", `/app/updates/${uid}/edit`); }
    if (!silent) toast.success(status === "published" ? "Update settings saved" : "Draft saved");
    return uid;
  };

  const handleSave = async () => { setSaving(true); try { await save(); } catch { toast.error("Save failed"); } finally { setSaving(false); } };

  const handlePublish = async () => {
    setPublishing(true);
    try {
      const uid = await save({ silent: true });
      const { data } = await api.post(`/updates/${uid}/publish`, {});
      setStatus("published");
      const sent = data.deliveries.filter((d) => d.status === "sent").length;
      const simulated = data.deliveries.filter((d) => d.status === "simulated").length;
      const failed = data.deliveries.filter((d) => d.status === "failed").length;
      const message = `Published. ${sent} emails sent, ${simulated} simulated, ${failed} failed.`;
      if (failed) toast.warning(message); else toast.success(message);
      navigate("/app/delivery");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Publish failed");
    } finally { setPublishing(false); }
  };

  const handleSchedule = async () => {
    if (!scheduledFor) return toast.error("Pick a date & time");
    setScheduling(true);
    try {
      const uid = await save({ silent: true });
      await api.post(`/updates/${uid}/schedule`, { scheduled_for: new Date(scheduledFor).toISOString() });
      setStatus("scheduled");
      toast.success("Update scheduled to auto-publish");
      navigate("/app/updates");
    } catch (err) { toast.error(err?.response?.data?.detail || "Schedule failed"); }
    finally { setScheduling(false); }
  };

  const handleUnschedule = async () => {
    try { await api.post(`/updates/${updateId}/unschedule`); setStatus("draft"); toast.success("Moved back to draft"); }
    catch { toast.error("Failed"); }
  };

  const previewUpdate = { title: title || `Update - ${period}`, period, tldr, body_markdown: body, metrics_snapshot: snapshot, published_at: status === "published" ? new Date().toISOString() : null };
  const attached = METRIC_DEFS.filter((d) => snapshot?.[d.key] !== null && snapshot?.[d.key] !== undefined && snapshot?.[d.key] !== "");

  const actions = (
    <div className="flex items-center gap-2">
      {status === "published" ? <Badge className="border-emerald-200 bg-emerald-50 text-emerald-700">Published</Badge> : <Badge className="border-slate-200 bg-slate-50 text-slate-700">Draft</Badge>}
      <Button variant="outline" onClick={handleSave} disabled={saving} className="gap-2" data-testid="save-draft-button">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
      </Button>
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button className="gap-2" disabled={publishing || readOnly || status === "imported"} data-testid="update-publish-button">
            {publishing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Publish
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent data-testid="publish-confirmation-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Publish this update?</AlertDialogTitle>
            <AlertDialogDescription>
              It will appear in the investor portal and be delivered to your {investorsCount} investor(s).
              {` Delivery: ${DELIVERY_LABELS[deliverySettings.mode]}.`}
              {investorsCount === 0 && " You have no investors yet - add some first on the Investors page."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="publish-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handlePublish} disabled={investorsCount === 0} data-testid="publish-confirm">Publish now</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );

  if (loading) return <AppShell title="Update"><p className="text-sm text-muted-foreground">Loading…</p></AppShell>;

  return (
    <AppShell title={updateId ? "Edit Update" : "New Update"} subtitle={company?.name} actions={actions}>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Editor */}
        <div className="min-w-0 space-y-4">
          {status !== "imported" && <UpdateDeliverySettings value={deliverySettings} onChange={setDeliverySettings} published={readOnly} />}
          <Card className="rounded-xl border bg-card p-5">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="period">Period</Label>
                <MonthPicker value={period} onChange={setPeriod} disabled={readOnly} className="mt-1" data-testid="composer-period" />
              </div>
              <div>
                <Label htmlFor="title">Title</Label>
                <Input id="title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder={`Update - ${period}`} className="mt-1" data-testid="composer-title" />
              </div>
            </div>
            {templates.length > 0 && (
              <div className="mt-4">
                <Label>Start from template</Label>
                <Select onValueChange={applyTemplate}>
                  <SelectTrigger className="mt-1" data-testid="composer-template"><SelectValue placeholder="Choose a template..." /></SelectTrigger>
                  <SelectContent>{templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
              <div className="text-sm font-medium text-foreground">Attached metrics</div>
              <Button variant="ghost" size="sm" className="gap-1.5 text-primary" onClick={pullMetrics} data-testid="pull-metrics-button">
                <RefreshCw className="h-3.5 w-3.5" /> Pull metrics for period
              </Button>
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {attached.length === 0 ? <span className="text-xs text-muted-foreground">No metrics attached. Click "Pull metrics for period".</span> :
                attached.map((d) => <Badge key={d.key} variant="secondary" className="font-normal">{d.label}: {formatMetric(d.type, snapshot[d.key])}</Badge>)}
            </div>
          </Card>

          <Card className="rounded-xl border bg-card p-5 space-y-4">
            <ListEditor label="Highlights" items={highlights} onChange={setHighlights} testId="composer-highlights" placeholder="A win this period..." />
            <ListEditor label="Lowlights & Challenges" items={lowlights} onChange={setLowlights} testId="composer-lowlights" placeholder="An honest challenge..." />
            <ListEditor label="Asks" items={asks} onChange={setAsks} testId="composer-asks" placeholder="How investors can help..." />
          </Card>

          <Card className="rounded-xl border bg-card p-5">
            <div className="mb-2 flex items-center justify-between">
              <Label htmlFor="tldr">TL;DR</Label>
            </div>
            <Input id="tldr" value={tldr} onChange={(e) => setTldr(e.target.value)} placeholder="One-line summary of the month" data-testid="composer-tldr" />

            <div className="mb-2 mt-4 flex flex-wrap items-center justify-between gap-2">
              <Label htmlFor="body">Narrative (Markdown)</Label>
              <Button size="sm" onClick={generate} disabled={generating} className="gap-1.5" data-testid="ai-generate-update-button">
                {generating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} Generate with AI
              </Button>
              <Button size="sm" variant="outline" onClick={insertSweatEquity} className="gap-1.5" data-testid="insert-sweat-equity">
                <HeartHandshake className="h-3.5 w-3.5" /> Insert sweat equity
              </Button>
            </div>
            <Textarea id="body" value={body} onChange={(e) => setBody(e.target.value)} className="min-h-[260px] font-mono text-xs" placeholder="Write your update, or generate a draft with AI and edit it here." data-testid="composer-body" />
          </Card>

          <Card className="rounded-xl border bg-card p-5">
            <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-foreground"><CalendarClock className="h-4 w-4 text-primary" /> Schedule auto-publish</div>
            {status === "scheduled" ? (
            <div data-testid="composer-scheduled-notice" className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
              <span className="text-sm text-amber-800">This update is scheduled to auto-publish.</span>
                <Button variant="outline" size="sm" onClick={handleUnschedule} data-testid="unschedule-button">Cancel schedule</Button>
              </div>
            ) : (
              <div className="flex flex-wrap items-end gap-2">
                <div className="min-w-0 max-w-full">
                  <Label htmlFor="sched" className="text-xs">Publish at</Label>
                  <input id="sched" type="datetime-local" value={scheduledFor} onChange={(e) => setScheduledFor(e.target.value)} className="mt-1 block w-full min-w-0 max-w-full rounded-md border bg-background px-3 py-2 text-sm" data-testid="schedule-datetime" />
                </div>
                <Button variant="outline" onClick={handleSchedule} disabled={scheduling || readOnly || status === "imported"} className="gap-2" data-testid="schedule-button">
                  {scheduling ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarClock className="h-4 w-4" />} Schedule
                </Button>
              </div>
            )}
          </Card>
        </div>

        {/* Preview */}
        <div className="min-w-0 lg:sticky lg:top-20 lg:self-start">
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Live preview</div>
          <Card className="rounded-xl border bg-card p-6" data-testid="update-preview-panel">
            {body || tldr ? (
              <><UpdateReader update={previewUpdate} company={company} /><TenXBrandFooter context="composer-preview" className="mt-6" /></>
            ) : (
              <div className="flex h-[320px] flex-col items-center justify-center text-center text-sm text-muted-foreground">
                <Sparkles className="mb-2 h-6 w-6 text-primary/40" />
                Your update preview will appear here. Add highlights and click "Generate with AI".
              </div>
            )}
          </Card>
        </div>
      </div>
      {updateId && status === "published" && (
        <Card className="mt-6 rounded-xl border bg-card p-6"><CommentsThread updateId={updateId} /></Card>
      )}
    </AppShell>
  );
}
